export interface BreakdownItem {
  id: string
  name: string
  category?: string
  value: number
  valueBRL?: number | null
  valueUSD?: number | null
  currency?: string
  isCustom?: boolean
  isPlatformFee?: boolean
  [key: string]: any
}

/**
 * Normalize a fee name for grouping: lowercase, strip accents, trim, collapse whitespace.
 * Examples: "Desconsolidação", "DESCONSOLIDAÇÃO", "Desconsolidacao" → "desconsolidacao".
 */
export function normalizeFeeName(name: string): string {
  return (name || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Consolidate breakdown items by normalized name.
 *
 * When multiple items share the same name (case/accents-insensitive),
 * they are merged into one with values summed. This is the view shown
 * to the shipper, and to the freight forwarder on summary screens.
 *
 * Platform fees (isPlatformFee=true) merged with custom items of the
 * same name produce a single consolidated row marked as platform fee
 * — the underlying split is intentionally hidden at this layer.
 */
export function consolidateBreakdown(breakdown: BreakdownItem[]): BreakdownItem[] {
  if (!Array.isArray(breakdown) || breakdown.length === 0) return []

  const groups = new Map<string, BreakdownItem>()

  for (const item of breakdown) {
    const key = normalizeFeeName(item.name)
    const existing = groups.get(key)

    if (!existing) {
      groups.set(key, { ...item })
      continue
    }

    // Same name across items with different currencies cannot be reliably
    // summed in the original `value` field — fall back to valueBRL/valueUSD
    // for the user-facing total. Preserve a stable currency (USD) when the
    // sources disagree.
    const sameCurrency = existing.currency === item.currency
    existing.value = sameCurrency
      ? (existing.value || 0) + (item.value || 0)
      : existing.value // keep first slot, totals come from valueBRL/USD below
    existing.valueBRL = (existing.valueBRL ?? 0) + (item.valueBRL ?? 0)
    existing.valueUSD = (existing.valueUSD ?? 0) + (item.valueUSD ?? 0)

    if (!sameCurrency) {
      // Force USD on the consolidated row so downstream rendering uses
      // valueUSD consistently rather than mixing currencies.
      existing.currency = 'USD'
      existing.value = existing.valueUSD ?? 0
    }

    existing.isPlatformFee = existing.isPlatformFee || item.isPlatformFee
    existing.isCustom = existing.isCustom && item.isCustom
  }

  return Array.from(groups.values())
}
